import { inject, Injectable } from "@angular/core";
import { ExceptionsService } from "@core-services/exceptions.service";
import {
	CreateLimitMarginOrderRequest,
	CreateMarginOfferRequest,
	EstimateLimitMarginOrderRequest,
	EstimateMarginOfferRequest,
	LeverageDirection,
	LimitMarginOrderBalanceResponse,
	TradeType,
	UnitDefinition,
	UpdateLimitMarginOrderRequest
} from "@gql/offer";
import {
	AssetFragment,
	AssetPageFragment,
	MarginPositionFragment,
	PortfolioMarginPositionsPageFragment
} from "app/api/graphql/bff/generated/fragments.service.graphql-gen";
import {
	GetAssetGQLService,
	GetAssetQueryVariables
} from "app/api/graphql/bff/generated/get-asset.service.graphql-gen";
import {
	GetAssetsGQLService,
	GetAssetsQueryVariables
} from "app/api/graphql/bff/generated/get-assets.service.graphql-gen";
import {
	GetMarginLimitOrderGQLService,
	GetMarginLimitOrderQuery
} from "app/api/graphql/bff/generated/get-margin-limit-order.service.graphql-gen";
import {
	GetMarginPositionGQLService,
	GetMarginPositionQueryVariables
} from "app/api/graphql/bff/generated/get-margin-position.service.graphql-gen";
import {
	GetMarginPositionsGQLService,
	GetMarginPositionsQueryVariables
} from "app/api/graphql/bff/generated/get-margin-positions.service.graphql-gen";
import { AssetOrderType, MarginLimitOrderInput } from "app/api/graphql/bff/generated/types.graphql-gen";
import { LimitMarginOrderBalanceGQLService } from "app/api/graphql/offer/generated/margin-trading.limit-order.balance.service.graphql-gen";
import {
	CreateLimitMarginOrderGQLService,
	CreateLimitMarginOrderMutation
} from "app/api/graphql/offer/generated/margin-trading.limit-order.create.service.graphql-gen";
import {
	EstimateLimitMarginOrderGQLService,
	EstimateLimitMarginOrderQuery
} from "app/api/graphql/offer/generated/margin-trading.limit-order.estimate.service.graphql-gen";
import {
	UpdateLimitMarginOrderGQLService,
	UpdateLimitMarginOrderMutation
} from "app/api/graphql/offer/generated/margin-trading.limit-order.update.service.graphql-gen";
import {
	AcceptMarginOfferGQLService,
	AcceptMarginOfferMutation
} from "app/api/graphql/offer/generated/margin-trading.offer.accept.service.graphql-gen";
import {
	CreateMarginOfferGQLService,
	CreateMarginOfferMutation
} from "app/api/graphql/offer/generated/margin-trading.offer.create.service.graphql-gen";

import {
	EstimateMarginOfferMutationGQLService,
	EstimateMarginOfferMutationMutation
} from "app/api/graphql/offer/generated/margin-trading.offer.estimate.service.graphql-gen";
import { TradingSettingsGQLService } from "app/api/graphql/offer/generated/margin-trading.tradingsettings.service.graphql-gen";
import { catchError, from, map, Observable } from "rxjs";

export interface IMTOfferRequest {
	amount: string;
	amountDefinition: UnitDefinition;
	assetId: string;
	leverage?: number;
	leverageDirection: LeverageDirection;
	tradeType: TradeType;
	tradeUnit: UnitDefinition;
}

@Injectable({
	providedIn: "root"
})
export class MarginTradingRemote {
	private acceptMarginOfferGQLService = inject(AcceptMarginOfferGQLService);
	private createMarginOfferGQLService = inject(CreateMarginOfferGQLService);
	private estimateMarginOfferMutationGQLService = inject(EstimateMarginOfferMutationGQLService);
	private tradingSettingsGQLService = inject(TradingSettingsGQLService);
	private getAssetsGQLService = inject(GetAssetsGQLService);
	private getMarginPositionGQLService = inject(GetMarginPositionGQLService);
	private getMarginPositionsGQLService = inject(GetMarginPositionsGQLService);
	private getAssetGQLService = inject(GetAssetGQLService);
	private exceptionsService = inject(ExceptionsService);

	//Limit orders
	private estimateLimitMarginOrderGQLService = inject(EstimateLimitMarginOrderGQLService);
	private createLimitMarginOrderGQLService = inject(CreateLimitMarginOrderGQLService);
	private limitMarginOrderBalanceGQLService = inject(LimitMarginOrderBalanceGQLService);
	private getMarginLimitOrderGQLService = inject(GetMarginLimitOrderGQLService);
	private updateMarginLimitOrderGQLService = inject(UpdateLimitMarginOrderGQLService);

	/**
	 * * accept the margin trade offer
	 * * @param {offerId} offerId - The request object containing the parameters for the margin offer.
	 * * @returns {Observable<AcceptMarginOfferMutation["acceptMarginOffer"]>} - An observable that emits the accepted margin offer.
	 * * @throws {Error} - Throws an error if the GraphQL mutation fails.
	 */
	acceptMarginOffer(offerId: string): Observable<AcceptMarginOfferMutation["acceptMarginOffer"]> {
		return this.acceptMarginOfferGQLService.mutate({ input: { offerId: offerId } }).pipe(
			map(({ data: { acceptMarginOffer } }) => {
				return acceptMarginOffer;
			}),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	/**
	 * * create the margin trade offer
	 * * @param {IMTOfferRequest} request - The request object containing the parameters for the margin offer.
	 * * @returns {Observable<CreateMarginOfferMutation["createMarginOffer"]>} - An observable that emits the created margin offer.
	 * * @throws {Error} - Throws an error if the GraphQL mutation fails.
	 */
	createMTOffer(request: IMTOfferRequest): Observable<CreateMarginOfferMutation> {
		const input = this.MTRequestDeserializer(request);

		return this.createMarginOfferGQLService.mutate({ input }).pipe(
			map((response) => {
				return response.data;
			}),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	/**
	 * * estimate the margin trade offer
	 * * @param {IMTOfferRequest} request - The request object containing the parameters for the margin offer.
	 * * @returns {Observable<EstimateMarginOfferMutationMutation["estimateMarginOffer"]>} - An observable that emits the estimated margin offer.
	 * * @throws {Error} - Throws an error if the GraphQL mutation fails.
	 */
	createMTOfferEstimate(
		request: IMTOfferRequest
	): Observable<EstimateMarginOfferMutationMutation["estimateMarginOffer"]> {
		const input = this.MTRequestDeserializer(request);
		return this.estimateMarginOfferMutationGQLService.mutate({ input }).pipe(
			map(({ data: { estimateMarginOffer } }) => {
				return estimateMarginOffer;
			}),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	private MTRequestDeserializer(request: IMTOfferRequest): EstimateMarginOfferRequest | CreateMarginOfferRequest {
		const { amount, amountDefinition, assetId, leverage, leverageDirection, tradeType, tradeUnit } = request;
		return {
			amount: { value: amount },
			amountDefinition,
			assetId,
			leverage: leverage ? +leverage : 0,
			leverageDirection,
			tradeType,
			tradeUnit
		};
	}

	/**
	 * * get the stable coin asset id
	 * * @returns {Observable<string>} - An observable that emits the stable coin asset id.
	 * * @throws {Error} - Throws an error if the GraphQL query fails.
	 */
	getStableCoinAssetId(): Observable<string> {
		return this.tradingSettingsGQLService.watch(null, { nextFetchPolicy: "network-only" }).valueChanges.pipe(
			map(
				({
					data: {
						tradingSettings: { marginTradingUnderlyingAssetId }
					}
				}) => marginTradingUnderlyingAssetId
			),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	/**
	 * * get the assets for margin trading
	 * * @param {GetAssetsQueryVariables} input - The input variables for the GraphQL query.
	 * * @returns {Observable<AssetPageFragment>} - An observable that emits the asset page fragment.
	 * * @throws {Error} - Throws an error if the GraphQL query fails.
	 */
	getAssets(
		input: GetAssetsQueryVariables = { includeOHLC: false, includeMaximumLeverage: false }
	): Observable<AssetPageFragment> {
		return this.getAssetsGQLService.watch(input, { nextFetchPolicy: "network-only" }).valueChanges.pipe(
			map(({ data: { assets } }) => assets),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	getAssetsFetch(
		input: GetAssetsQueryVariables = { includeOHLC: false, includeMaximumLeverage: false }
	): Observable<AssetPageFragment> {
		return this.getAssetsGQLService.fetch(input, { fetchPolicy: "network-only" }).pipe(
			map(({ data: { assets } }) => assets),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	getAsset(input: GetAssetQueryVariables): Observable<AssetFragment> {
		return this.getAssetGQLService.watch(input, { nextFetchPolicy: "network-only" }).valueChanges.pipe(
			map(({ data: { asset } }) => asset as AssetFragment),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	isMarginTradingEligible$(assetSymbol?: string): Observable<boolean> {
		return this.getAssets({
			input: {
				query: assetSymbol,
				filters: {
					orderTypes: [AssetOrderType.MarginLong]
				}
			},
			includeOHLC: false,
			includeMaximumLeverage: true
		}).pipe(
			map(({ edges }: AssetPageFragment) => {
				const validCryptoMargin = edges?.find(
					({ node }) =>
						node?.__typename === "CryptoAsset" &&
						node?.marginLongSettings?.buyActive &&
						node?.marginLongSettings?.maximumLeverage &&
						node?.symbol === assetSymbol
				);
				return !!validCryptoMargin;
			})
		);
	}

	/**
	 * * get the margin position for the current user
	 * * @param {GetMarginPositionQueryVariables} input - The input variables for the GraphQL query.
	 * * @returns {Observable<MarginPositionFragment>} - An observable that emits the margin position.
	 * * @throws {Error} - Throws an error if the GraphQL query fails.
	 */
	getMarginPosition(input: GetMarginPositionQueryVariables): Observable<MarginPositionFragment> {
		return this.getMarginPositionGQLService.fetch(input, { fetchPolicy: "network-only" }).pipe(
			map(({ data: { portfolio } }) => portfolio.marginPosition),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	getMarginPositionFetch(input: GetMarginPositionQueryVariables): Observable<MarginPositionFragment> {
		return from(this.getMarginPositionGQLService.fetch(input, { fetchPolicy: "network-only" })).pipe(
			map(({ data: { portfolio } }) => portfolio.marginPosition),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	/**
	 * * get the margin positions for the current user
	 * * @param {GetMarginPositionsQueryVariables} input - The input variables for the GraphQL query.
	 * * @returns {Observable<PortfolioMarginPositionsPageFragment>} - An observable that emits the portfolio margin positions page fragment.
	 * * @throws {Error} - Throws an error if the GraphQL query fails.
	 */
	getMarginPositions(input: GetMarginPositionsQueryVariables): Observable<PortfolioMarginPositionsPageFragment> {
		return this.getMarginPositionsGQLService.watch(input, { nextFetchPolicy: "network-only" }).valueChanges.pipe(
			map(({ data: { portfolio } }) => portfolio.marginPositions),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	getMarginPositionsFetch(input: GetMarginPositionsQueryVariables): Observable<PortfolioMarginPositionsPageFragment> {
		return this.getMarginPositionsGQLService.fetch(input, { fetchPolicy: "network-only" }).pipe(
			map(({ data: { portfolio } }) => portfolio.marginPositions),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	/**
	 * * estimate the limit margin order
	 * * @param {string} assetId - The request asset id.
	 * * @returns {Observable<LimitMarginOrderBalanceResponse>} - An observable that emits the estimated limit margin order response.
	 * * @throws {Error} - Throws an error if the GraphQL query fails.
	 */
	getMarginLimitOrderBalance(assetId: string): Observable<LimitMarginOrderBalanceResponse> {
		return this.limitMarginOrderBalanceGQLService.fetch({ assetId }, { fetchPolicy: "network-only" }).pipe(
			map(({ data: { limitMarginOrderBalance } }) => limitMarginOrderBalance),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	/**
	 * * get the estimate for a limit margin order
	 * * @param {EstimateLimitMarginOrderRequest} request - The request object containing the parameters for the limit margin order estimate.
	 * * @returns {Observable<EstimateLimitMarginOrderQuery>} - An observable that emits the estimated limit margin order response.
	 * * @throws {Error} - Throws an error if the GraphQL mutation fails.
	 */
	getMarginLimitOrderEstimate(request: EstimateLimitMarginOrderRequest): Observable<EstimateLimitMarginOrderQuery> {
		return this.estimateLimitMarginOrderGQLService.fetch({ input: request }, { fetchPolicy: "network-only" }).pipe(
			map(({ data }) => data),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	/**
	 * * create a limit margin order
	 * * @param {CreateLimitMarginOrderRequest} request - The request object containing the parameters for the limit margin order creation.
	 * * @returns {Observable<CreateLimitMarginOrderMutation>} - An observable that emits the created limit margin order response.
	 * * @throws {Error} - Throws an error if the GraphQL mutation fails.
	 */
	createMTLimitOrder(request: CreateLimitMarginOrderRequest): Observable<CreateLimitMarginOrderMutation> {
		return this.createLimitMarginOrderGQLService.mutate({ request }).pipe(
			map((response) => {
				return response.data;
			}),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	/**
	 * * get the details of a margin limit order
	 * * @param {MarginLimitOrderInput} request - The request object containing the parameters for the margin limit order query.
	 * * @returns {Observable<GetMarginLimitOrderQuery>} - An observable that emits the margin limit order details.
	 * * @throws {Error} - Throws an error if the GraphQL query fails.
	 */
	getMTOrderDetail(input: MarginLimitOrderInput): Observable<GetMarginLimitOrderQuery> {
		return this.getMarginLimitOrderGQLService.fetch({ input }, { fetchPolicy: "network-only" }).pipe(
			map(({ data }) => data),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	/**
	 * * update a margin limit order
	 * * @param {UpdateLimitMarginOrderRequest} request - The request object containing the parameters for the margin limit order update.
	 * * @returns {Observable<UpdateLimitMarginOrderMutation>} - An observable that emits the updated margin limit order response.
	 * * @throws {Error} - Throws an error if the GraphQL mutation fails.
	 */
	updateMTOrder(request: UpdateLimitMarginOrderRequest): Observable<UpdateLimitMarginOrderMutation> {
		return this.updateMarginLimitOrderGQLService.mutate({ request }).pipe(
			map((response) => {
				return response.data;
			}),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}
}
